<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { debounce } from '@chatwoot/utils';

import Button from 'dashboard/components-next/button/Button.vue';
import ListToolbar from 'dashboard/components-next/polaris/cards/ListToolbar.vue';
import BulkSelectBar from 'dashboard/components-next/polaris/assistant/BulkSelectBar.vue';
import DeleteDialog from 'dashboard/components-next/polaris/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/polaris/pageComponents/BulkDeleteDialog.vue';
import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import ResponseCardSkin from 'dashboard/components-next/polaris/skin/ResponseCardSkin.vue';
import ResponsesTabs from 'dashboard/components-next/polaris/pageComponents/response/ResponsesTabs.vue';
import CreateResponseDialog from 'dashboard/components-next/polaris/pageComponents/response/CreateResponseDialog.vue';
import ResponsePageEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/ResponsePageEmptyState.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const uiFlags = useMapGetter('polarisResponses/getUIFlags');
const responseMeta = useMapGetter('polarisResponses/getMeta');
const responses = useMapGetter('polarisResponses/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);

const selectedResponse = ref(null);
const deleteDialog = ref(null);
const bulkDeleteDialog = ref(null);

const selectedAssistantId = computed(() => route.params.assistantId);
const dialogType = ref('');
const searchQuery = ref('');
const { t } = useI18n();

const createDialog = ref(null);

// Filter out approved responses in pending view
const filteredResponses = computed(() =>
  responses.value.filter(response => response.status !== 'approved')
);

const handleDelete = () => {
  deleteDialog.value.dialogRef.open();
};

const handleAccept = async () => {
  try {
    await store.dispatch('polarisResponses/update', {
      id: selectedResponse.value.id,
      status: 'approved',
    });
    useAlert(t(`POLARIS.RESPONSES.EDIT.APPROVE_SUCCESS_MESSAGE`));
    store.dispatch(
      'polarisResponses/fetchPendingCount',
      selectedAssistantId.value
    );
  } catch (error) {
    const errorMessage =
      error?.message || t(`POLARIS.RESPONSES.EDIT.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    selectedResponse.value = null;
  }
};

const handleEdit = () => {
  dialogType.value = 'edit';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  selectedResponse.value = filteredResponses.value.find(
    response => id === response.id
  );
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
    if (action === 'edit') {
      handleEdit();
    }
    if (action === 'approve') {
      handleAccept();
    }
  });
};

const handleNavigationAction = ({ id, type }) => {
  if (type === 'Conversation') {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: id },
    });
  }
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedResponse.value = null;
};

const updateURLWithFilters = (page, search) => {
  const query = {
    page: page || 1,
  };

  if (search) {
    query.search = search;
  }

  router.replace({ query });
};

const fetchResponses = (page = 1) => {
  const filterParams = { page, status: 'pending' };

  if (selectedAssistantId.value) {
    filterParams.assistantId = selectedAssistantId.value;
  }
  if (searchQuery.value) {
    filterParams.search = searchQuery.value;
  }

  // Update URL with current filters
  updateURLWithFilters(page, searchQuery.value);

  store.dispatch('polarisResponses/get', filterParams);
};

// Bulk action
const bulkSelectedIds = ref(new Set());

const buildSelectedCountLabel = computed(() => {
  const count = filteredResponses.value?.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('POLARIS.RESPONSES.UNSELECT_ALL', { count })
    : t('POLARIS.RESPONSES.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('POLARIS.RESPONSES.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleCardSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const fetchResponseAfterBulkAction = () => {
  const hasNoResponsesLeft = filteredResponses.value?.length === 0;
  const currentPage = responseMeta.value?.page;

  if (hasNoResponsesLeft) {
    const pageToFetch = currentPage > 1 ? currentPage - 1 : currentPage;
    fetchResponses(pageToFetch);
  } else {
    fetchResponses(currentPage);
  }

  bulkSelectedIds.value = new Set();
};

const handleBulkApprove = async () => {
  try {
    await store.dispatch(
      'polarisBulkActions/handleBulkApprove',
      Array.from(bulkSelectedIds.value)
    );

    fetchResponseAfterBulkAction();
    store.dispatch(
      'polarisResponses/fetchPendingCount',
      selectedAssistantId.value
    );
    useAlert(t('POLARIS.RESPONSES.BULK_APPROVE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message || t('POLARIS.RESPONSES.BULK_APPROVE.ERROR_MESSAGE')
    );
  }
};

const onPageChange = page => {
  const hadSelection = bulkSelectedIds.value.size > 0;

  fetchResponses(page);

  if (hadSelection) {
    bulkSelectedIds.value = new Set();
  }
};

// The "Pending (n)" tab follows every change to the queue: approve, and delete too.
const refreshPendingCount = () => {
  store.dispatch(
    'polarisResponses/fetchPendingCount',
    selectedAssistantId.value
  );
};

const onDeleteSuccess = () => {
  refreshPendingCount();
  if (filteredResponses.value?.length === 0 && responseMeta.value?.page > 1) {
    onPageChange(responseMeta.value.page - 1);
  }
};

const onBulkDeleteSuccess = () => {
  refreshPendingCount();
  fetchResponseAfterBulkAction();
};

const debouncedSearch = debounce(async () => {
  fetchResponses(1);
}, 500);

const hasActiveFilters = computed(() => {
  return Boolean(searchQuery.value);
});

const clearFilters = () => {
  searchQuery.value = '';
  fetchResponses(1);
};

const initializeFromURL = () => {
  if (route.query.search) {
    searchQuery.value = route.query.search;
  }
  const pageFromURL = parseInt(route.query.page, 10) || 1;
  fetchResponses(pageFromURL);
};

onMounted(() => {
  initializeFromURL();
});
</script>

<template>
  <PageLayout
    preview="open"
    :total-count="responseMeta.totalCount"
    :current-page="responseMeta.page"
    :header-title="$t('POLARIS.RESPONSES.HEADER')"
    :is-fetching="isFetching"
    :is-empty="!filteredResponses.length"
    :show-pagination-footer="!isFetching && !!filteredResponses.length"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.POLARIS"
    @update:current-page="onPageChange"
  >
    <template #tabs>
      <ResponsesTabs />
    </template>

    <template #subHeader>
      <ListToolbar
        v-if="bulkSelectedIds.size === 0"
        v-model:search="searchQuery"
        :placeholder="$t('POLARIS.RESPONSES.SEARCH_PLACEHOLDER')"
        :count="filteredResponses.length ? responseMeta.totalCount : null"
        count-key="POLARIS.PAGES.RESPONSES_COUNT"
        @update:search="debouncedSearch"
      />
      <BulkSelectBar
        v-else
        v-model="bulkSelectedIds"
        :all-items="filteredResponses"
        :select-all-label="buildSelectedCountLabel"
        :selected-count-label="selectedCountLabel"
        :delete-label="$t('POLARIS.RESPONSES.BULK_DELETE_BUTTON')"
        @bulk-delete="bulkDeleteDialog.dialogRef.open()"
      >
        <template #secondaryActions>
          <Button
            :label="$t('POLARIS.RESPONSES.BULK_APPROVE_BUTTON')"
            sm
            ghost
            icon="i-lucide-check"
            class="!px-1.5"
            @click="handleBulkApprove"
          />
        </template>
      </BulkSelectBar>
    </template>

    <template #emptyState>
      <ResponsePageEmptyState
        variant="pending"
        :has-active-filters="hasActiveFilters"
        @clear-filters="clearFilters"
      />
    </template>

    <template #paywall />

    <template #body>
      <div class="flex flex-col gap-4">
        <ResponseCardSkin
          v-for="response in filteredResponses"
          :key="response.id"
          :response="response"
          selectable
          :selected="bulkSelectedIds.has(response.id)"
          @action="handleAction"
          @select="handleCardSelect"
          @navigate="handleNavigationAction"
        />
      </div>
    </template>

    <DeleteDialog
      v-if="selectedResponse"
      ref="deleteDialog"
      :entity="selectedResponse"
      type="Responses"
      @delete-success="onDeleteSuccess"
    />

    <BulkDeleteDialog
      v-if="bulkSelectedIds"
      ref="bulkDeleteDialog"
      :bulk-ids="bulkSelectedIds"
      type="AssistantResponse"
      @delete-success="onBulkDeleteSuccess"
    />

    <CreateResponseDialog
      v-if="dialogType"
      ref="createDialog"
      :type="dialogType"
      :selected-response="selectedResponse"
      @close="handleCreateClose"
    />
  </PageLayout>
</template>
